import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Ban, XCircle } from 'lucide-react';

interface OpportunityActionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAction: 'lost' | 'cancelled' | null;
  selectedOpportunity: any;
  selectedReason: string;
  onReasonChange: (reason: string) => void;
  cancelledReasons: Array<{ value: string }>;
  onConfirm: () => void;
  onCancel: () => void;
}

export function OpportunityActionDialog({
  isOpen,
  onOpenChange,
  selectedAction,
  selectedOpportunity,
  selectedReason,
  onReasonChange,
  cancelledReasons,
  onConfirm,
  onCancel
}: OpportunityActionDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="glass-dialog max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedAction === 'lost' ? 'Mark Opportunity as Lost' : 'Cancel Opportunity'}
          </DialogTitle>
          <DialogDescription>
            Please select a reason for {selectedAction === 'lost' ? 'losing' : 'cancelling'} this opportunity.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Opportunity Details */}
          <div className="glass-card p-4 space-y-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-medium">{selectedOpportunity?.end_user.name}</p>
                <p className="text-sm text-muted-foreground">{selectedOpportunity?.opportunity_id}</p>
              </div>
              <Badge variant="outline" className="glass-badge">
                {selectedOpportunity?.opportunity_value}
              </Badge>
            </div>
          </div>

          {/* Reason Selection */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium">
              Reason <span className="text-worldsview-red">*</span>
            </Label>
            <Select value={selectedReason} onValueChange={onReasonChange}>
              <SelectTrigger id="reason" className="glass-input">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {cancelledReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!selectedReason}
            className={selectedAction === 'lost' 
              ? 'bg-worldsview-orange hover:bg-worldsview-orange/90' 
              : 'bg-worldsview-red hover:bg-worldsview-red/90'
            }
          >
            {selectedAction === 'lost' ? (
              <>
                <XCircle className="w-4 h-4 mr-2" />
                Mark as Lost
              </>
            ) : (
              <>
                <Ban className="w-4 h-4 mr-2" />
                Cancel Opportunity
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
